import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Wifi, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EnhancedLoadingFallbackProps {
  timeout?: number;
  retryAction?: () => void;
  showStaticContent?: boolean;
}

export const EnhancedLoadingFallback = ({ 
  timeout = 10000, 
  retryAction,
  showStaticContent = false 
}: EnhancedLoadingFallbackProps) => {
  const [isTimeout, setIsTimeout] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsTimeout(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout, retryCount]);

  const handleRetry = () => {
    setIsTimeout(false);
    setRetryCount(prev => prev + 1);
    if (retryAction) {
      retryAction();
    } else {
      window.location.reload();
    }
  };

  // Detect if connection might be slow or offline
  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;

  if (isTimeout || isOffline) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            {isOffline ? 
              'Vypadá to, že jste offline. Zkontrolujte prosím připojení k internetu.' :
              'Načítání trvá déle než obvykle. Možná máte pomalé připojení nebo vysokou zátěž serveru.'
            }
          </AlertDescription>
        </Alert>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={handleRetry} 
            variant="outline" 
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Zkusit znovu
          </Button>
          {isOffline && (
            <Button 
              onClick={() => window.location.reload()} 
              variant="default"
              className="flex items-center gap-2"
            >
              <Wifi className="h-4 w-4" />
              Obnovit stránku
            </Button>
          )}
        </div>

        {showStaticContent && (
          <div className="mt-8">
            <StaticContent />
          </div>
        )}
      </div>
    );
  }

  return <GlobalLoadingFallback />;
};

const StaticContent = () => (
  <div className="space-y-6 text-center">
    <h1 className="text-3xl font-bold text-foreground">Horsebeat.cz</h1>
    <p className="text-lg text-muted-foreground">
      Kompletní databáze dostihových koní, žokejů a výsledků závodů
    </p>
    
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
      <Card>
        <CardHeader>
          <h3 className="font-semibold">🏇 Koně</h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Databáze dostihových koní s podrobnými statistikami
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <h3 className="font-semibold">👤 Žokejové</h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Profily žokejů a jejich úspěšnost
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <h3 className="font-semibold">🏁 Výsledky</h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Aktuální a historické výsledky dostihů
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <h3 className="font-semibold">📊 Analýzy</h3>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Analýzy kurzů a dostihových dat
          </p>
        </CardContent>
      </Card>
    </div>
  </div>
);

const GlobalLoadingFallback = () => (
  <div className="container mx-auto px-4 py-8">
    <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="text-muted-foreground">Načítám data...</p>
      <div className="w-full max-w-md space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  </div>
);

export { GlobalLoadingFallback };